import { supabase } from '@/lib/supabase';
import type { ProductCatalog, ProductSubscription } from '@/types';

export interface ProductStats {
  product_code: string;
  active_count: number;
  trial_count: number;
  total_revenue_agorot: number;
}

export interface SubscriptionWithOrg extends ProductSubscription {
  organizations: { id: string; name: string } | null;
}

export const productsApi = {
  // DEPRECATED — migrated to licensingApi. Remove after confirming no other consumers.
  list: async (): Promise<ProductCatalog[]> => {
    const { data, error } = await supabase
      .from('product_catalog')
      .select('*')
      .order('position');

    if (error) throw error;
    return data as ProductCatalog[];
  },

  create: async (data: Omit<ProductCatalog, 'id' | 'created_at'>): Promise<ProductCatalog> => {
    const { data: product, error } = await supabase
      .from('product_catalog')
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return product as ProductCatalog;
  },

  update: async (id: string, data: Partial<ProductCatalog>): Promise<ProductCatalog> => {
    const { data: product, error } = await supabase
      .from('product_catalog')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return product as ProductCatalog;
  },

  toggleActive: async (id: string, isActive: boolean): Promise<void> => {
    const { error } = await supabase
      .from('product_catalog')
      .update({ is_active: isActive })
      .eq('id', id);

    if (error) throw error;
  },

  // DEPRECATED — migrated to licensingApi.getSystemStats. Remove after confirming no other consumers.
  /** Subscription counts & revenue per product */
  getStats: async (): Promise<ProductStats[]> => {
    const { data, error } = await supabase
      .from('product_subscriptions')
      .select('product_code, status, price_agorot');

    if (error) throw error;
    if (!data) return [];

    const map: Record<string, ProductStats> = {};
    for (const row of data) {
      if (!map[row.product_code]) {
        map[row.product_code] = {
          product_code: row.product_code,
          active_count: 0,
          trial_count: 0,
          total_revenue_agorot: 0,
        };
      }
      if (row.status === 'active') {
        map[row.product_code].active_count++;
        map[row.product_code].total_revenue_agorot += row.price_agorot ?? 0;
      } else if (row.status === 'trial') {
        map[row.product_code].trial_count++;
      }
    }
    return Object.values(map);
  },

  // ─── Subscriptions (per org) ───────────────────────────────────────────────

  getSubscriptions: async (orgId: string): Promise<ProductSubscription[]> => {
    const { data, error } = await supabase
      .from('product_subscriptions')
      .select('*')
      .eq('organization_id', orgId);

    if (error) throw error;
    return data as ProductSubscription[];
  },

  upsertSubscription: async (
    orgId: string,
    productCode: string,
    data: Partial<ProductSubscription>
  ): Promise<ProductSubscription> => {
    const { data: sub, error } = await supabase
      .from('product_subscriptions')
      .upsert(
        { organization_id: orgId, product_code: productCode, ...data },
        { onConflict: 'organization_id,product_code' }
      )
      .select()
      .single();

    if (error) throw error;
    return sub as ProductSubscription;
  },

  // DEPRECATED — migrated to licensingApi.listSubscriptions. Remove after confirming no other consumers.
  /** All subscriptions across all orgs, with org name */
  getAllSubscriptions: async (): Promise<SubscriptionWithOrg[]> => {
    const { data, error } = await supabase
      .from('product_subscriptions')
      .select('*, organizations(id, name)')
      .order('updated_at', { ascending: false });

    if (error) throw error;
    return (data ?? []) as SubscriptionWithOrg[];
  },

  cancelSubscription: async (subId: string): Promise<void> => {
    const { error } = await supabase
      .from('product_subscriptions')
      .update({ status: 'cancelled' })
      .eq('id', subId);

    if (error) throw error;
  },

  /** Sync a user's plan to the Dugri product DB via edge function */
  syncDugriPlan: async (
    email: string,
    plan: 'free' | 'pro' | 'team'
  ): Promise<{ success: boolean; email: string; previous_plan: string; new_plan: string }> => {
    const url = import.meta.env.VITE_DUGRI_ADMIN_URL;
    const apiKey = import.meta.env.VITE_DUGRI_ADMIN_KEY;

    if (!url || !apiKey) throw new Error('Dugri env vars not configured (VITE_DUGRI_ADMIN_URL / VITE_DUGRI_ADMIN_KEY)');

    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, plan, api_key: apiKey }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `HTTP ${res.status}`);
    }

    return res.json();
  },
};

export interface JumpstartChatPlan {
  id: string;
  system_code: string;
  code: string;
  name: string;
  description: string | null;
  price_monthly_agorot: number;
  price_yearly_agorot: number;
  features: Record<string, any>;
  limits: Record<string, any>;
  sort_order: number;
  is_active: boolean;
  created_at: string;
}

export interface JumpstartChatSubscription {
  id: string;
  org_id: string;
  system_code: string;
  plan_code: string;
  status: string;
  billing_cycle_months: number;
  current_period_start: string;
  current_period_end: string | null;
  created_at: string;
  updated_at: string;
  org_name: string | null;
  slug: string | null;
}

export async function getJumpstartChatPlans(): Promise<JumpstartChatPlan[]> {
  const { data, error } = await supabase
    .from('system_license_plans')
    .select('*')
    .eq('system_code', 'jumpstart-chat')
    .order('sort_order');

  if (error) throw error;
  return (data ?? []) as JumpstartChatPlan[];
}

export async function getJumpstartChatSubscriptions(): Promise<JumpstartChatSubscription[]> {
  const { data, error } = await supabase
    .from('system_subscriptions')
    .select('*, organizations(name, slug)')
    .eq('system_code', 'jumpstart-chat')
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (data ?? []).map((row: any) => ({
    ...row,
    org_name: row.organizations?.name ?? null,
    slug: row.organizations?.slug ?? null,
  })) as JumpstartChatSubscription[];
}

/** JumpStart Chat subscription row for an org (for trial-aware UI). */
export async function getJumpstartChatSubscriptionForOrg(
  orgId: string
): Promise<{ status: string } | null> {
  const { data, error } = await supabase
    .from('system_subscriptions')
    .select('status')
    .eq('org_id', orgId)
    .eq('system_code', 'jumpstart-chat')
    .maybeSingle();

  if (error) throw error;
  return data ? { status: String(data.status) } : null;
}

export async function assignJumpstartChatPlan(
  orgId: string,
  planCode: string,
  billingCycleMonths: number,
  status: 'active' | 'trial' = 'active',
  trialEndsAt?: string | null
): Promise<void> {
  const now = new Date();
  let periodEnd: Date;

  if (status === 'trial') {
    if (trialEndsAt) {
      periodEnd = new Date(trialEndsAt);
      if (Number.isNaN(periodEnd.getTime())) {
        throw new Error('Invalid trial end date');
      }
    } else {
      const { data: planRow } = await supabase
        .from('system_license_plans')
        .select('features')
        .eq('system_code', 'jumpstart-chat')
        .eq('code', planCode)
        .maybeSingle();
      const rawDays = (planRow?.features as Record<string, unknown> | null)?.trial_days;
      const days = typeof rawDays === 'number' && Number.isFinite(rawDays) ? rawDays : 14;
      periodEnd = new Date(now);
      periodEnd.setDate(periodEnd.getDate() + days);
    }
  } else {
    periodEnd = new Date(now);
    periodEnd.setMonth(periodEnd.getMonth() + billingCycleMonths);
  }

  const { error } = await supabase
    .from('system_subscriptions')
    .upsert(
      {
        org_id: orgId,
        system_code: 'jumpstart-chat',
        plan_code: planCode,
        status,
        billing_cycle_months: billingCycleMonths,
        current_period_start: now.toISOString(),
        current_period_end: periodEnd.toISOString(),
      },
      { onConflict: 'org_id,system_code' }
    );

  if (error) throw error;
}
